// ── Adapter Supabase do ICardRepository ───────────────────────────
// Única camada que conhece a RPC mark_card_lost e a tabela cards.

import { getSupabase } from '@infra/supabase/client';
import type { Database } from '@/types/database.types';
import type {
  ICardRepository,
  MarkCardLostResult,
  CardActiveWithGraph,
  CardLostWithGraph,
  CardFinancialCount,
  OverwriteCardForMoveParams,
} from '@modules/cards';

type CardUpdate = Database['public']['Tables']['cards']['Update'];

const requireSupabase = () => {
  const s = getSupabase();
  if (!s) throw new Error('Supabase nao inicializado.');
  return s;
};

const SELECT_ACTIVE = 'id, lead_id, pipeline_id, stage_id, ativo, status, sdr_id, closer_id, faturamento, won_at, lost_at, pipelines ( id, name, role, funnel_id, loss_reasons, funnel:funnel_id ( id, name ) ), pipeline_stages ( id, name, color )';
const SELECT_LOST = 'id, linked_card_id, pipeline_id, status, lost_at, loss_reason, pipelines ( name, role )';

export class SupabaseCardRepository implements ICardRepository {
  markCardLost = async (cardId: string, lossReason: string): Promise<MarkCardLostResult> => {
    const { data, error } = await requireSupabase().rpc('mark_card_lost', {
      p_card_id:     cardId,
      p_loss_reason: lossReason,
    });
    if (error) throw error;
    return (data ?? null) as unknown as MarkCardLostResult;
  };

  listActiveCardsByLead = async (leadId: string): Promise<CardActiveWithGraph[]> => {
    const { data } = await requireSupabase()
      .from('cards')
      .select(SELECT_ACTIVE)
      .eq('lead_id', leadId)
      .eq('ativo', true)
      .is('lost_at', null);
    return (data || []) as unknown as CardActiveWithGraph[];
  };

  listLostCardsByLead = async (leadId: string): Promise<CardLostWithGraph[]> => {
    const { data } = await requireSupabase()
      .from('cards')
      .select(SELECT_LOST)
      .eq('lead_id', leadId)
      .eq('status', 'lost');
    return (data || []) as unknown as CardLostWithGraph[];
  };

  reactivateCards = async (cardIds: string[]): Promise<void> => {
    const { error } = await requireSupabase()
      .from('cards')
      .update({ status: 'active', ativo: true, lost_at: null, loss_reason: null })
      .in('id', cardIds);
    if (error) throw error;
  };

  deactivateCard = async (cardId: string): Promise<void> => {
    const { error } = await requireSupabase().from('cards').update({ ativo: false }).eq('id', cardId);
    if (error) throw error;
  };

  deleteCard = async (cardId: string): Promise<void> => {
    const { error } = await requireSupabase().from('cards').delete().eq('id', cardId);
    if (error) throw error;
  };

  countCardFinancials = async (cardId: string): Promise<CardFinancialCount> => {
    const supabase = requireSupabase();
    const [{ count: cCount }, { count: sCount }] = await Promise.all([
      supabase.from('commissions').select('id', { count: 'exact', head: true }).eq('card_id', cardId),
      supabase.from('sales').select('id', { count: 'exact', head: true }).eq('card_id', cardId),
    ]);
    return { commissions: cCount || 0, sales: sCount || 0 };
  };

  assignActiveCardsOwner = async (
    leadId: string,
    field: 'sdr_id' | 'closer_id',
    userId: string | null,
  ): Promise<void> => {
    const { error } = await requireSupabase()
      .from('cards')
      .update({ [field]: userId || null } as unknown as CardUpdate)
      .eq('lead_id', leadId)
      .eq('ativo', true);
    if (error) throw error;
  };

  moveCardToStage = async (cardId: string, stageId: string): Promise<void> => {
    // UPDATE bloqueado por RLS pode retornar HTTP 200 com zero linhas, sem
    // preencher `error`. Exigir o registro atualizado impede que a UI trate
    // esse falso sucesso como persistência e só descubra no próximo refresh.
    const { data, error } = await requireSupabase()
      .from('cards')
      .update({ stage_id: stageId })
      .eq('id', cardId)
      .select('id, stage_id')
      .maybeSingle();
    if (error) throw error;
    if (!data) {
      throw new Error('O banco não confirmou a movimentação do card. Verifique sua permissão nesta pipeline.');
    }
    if (data.stage_id !== stageId) {
      throw new Error('O banco devolveu o card em uma etapa diferente da solicitada.');
    }
  };

  deactivateCardsByLead = async (leadId: string): Promise<void> => {
    const { error } = await requireSupabase().from('cards').update({ ativo: false }).eq('lead_id', leadId);
    if (error) throw error;
  };

  moveCardToPipeline = async (cardId: string, pipelineId: string, stageId: string): Promise<void> => {
    const { error } = await requireSupabase()
      .from('cards')
      .update({ pipeline_id: pipelineId, stage_id: stageId })
      .eq('id', cardId);
    if (error) throw error;
  };

  overwriteCardForMove = async (cardId: string, params: OverwriteCardForMoveParams): Promise<void> => {
    const { error } = await requireSupabase()
      .from('cards')
      .update({
        ativo: true,
        status: params.status,
        stage_id: params.stageId,
        sdr_id: params.sdrId,
        closer_id: params.closerId,
        linked_card_id: params.linkedCardId,
      })
      .eq('id', cardId);
    if (error) throw error;
  };
}
